import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import { API } from "../../api-services/ApiList";
import { storeToken, storeUser } from "../../api-services/Process";
import { SITELINKS } from "../../app-routes/links";
import { useGlobalState } from "../../context/GlobalStateContext";
import { getErrorMessage } from "../../utils";
import AuthLayout from "./component/AuthLayout";
import {
  AuthHeading,
  BackLink,
  CodeInput,
  FormAlert,
  SubmitButton,
  textLinkClass,
} from "./component/FormControls";

const CODE_LIFETIME = 600; // seconds (10 minutes)

// Format seconds as MM:SS
const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const AdminVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useGlobalState();

  const email = state.email ?? "";

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(CODE_LIFETIME);

  // Only reachable straight after the password step
  useEffect(() => {
    if (!location.state || !location.state.fromLogin) {
      navigate(SITELINKS.SIGNIN);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timeLeft]);

  const handle2FA = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (otp.length !== 6) {
      setError("Enter all 6 digits of the code.");
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post(API.LOGIN_2FA, { email, code: otp });
      storeToken(res.data?.access_token);
      storeUser(res.data?.user);
      navigate(SITELINKS.DASHBOARD);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setError("");
    setNotice("");
    if (!email) {
      setError("Your session has expired. Go back and sign in again.");
      return;
    }

    setResending(true);
    try {
      await apiClient.post(API.RESEND_2FA_CODE, { email });
      setOtp("");
      setTimeLeft(CODE_LIFETIME);
      setNotice(`A new code is on its way to ${email}.`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  const expired = timeLeft <= 0;

  return (
    <AuthLayout title="Verify sign-in | HoneConnect Admin">
      <BackLink to={SITELINKS.SIGNIN} label="Back to sign in" />

      <AuthHeading
        title="Check your email"
        description={
          <>
            Enter the 6-digit code we sent to{" "}
            <span className="font-medium text-honeConnectBlack">{email}</span>.
          </>
        }
      />

      {!error && <FormAlert tone="success" message={notice} />}
      <FormAlert tone="error" message={error} />

      <form onSubmit={handle2FA} noValidate className="space-y-5">
        <CodeInput
          value={otp}
          onChange={(v) => {
            setOtp(v);
            if (error) setError("");
          }}
        />

        <p
          className={`text-sm ${
            expired ? "font-medium text-red-600" : "text-honeConnectGray"
          }`}
        >
          {expired
            ? "This code has expired. Request a new one below."
            : `Code expires in ${formatTime(timeLeft)}`}
        </p>

        <div className="pt-3">
          <SubmitButton loading={loading} loadingText="Verifying" disabled={expired}>
            Verify and sign in
          </SubmitButton>
        </div>
      </form>

      <p className="mt-8 text-center text-sm text-honeConnectGray">
        Didn't get the code?{" "}
        <button
          type="button"
          onClick={handleResend2FA}
          disabled={!expired || resending}
          className={textLinkClass}
        >
          {resending ? "Sending" : "Resend code"}
        </button>
        {!expired && (
          <span className="block pt-1 text-xs text-gray-400">
            You can request a new code once this one expires.
          </span>
        )}
      </p>
    </AuthLayout>
  );
};

export default AdminVerify;
